import React, { CSSProperties, FC, useEffect, useMemo, useState } from 'react'
import { Alert, Button, Empty, Flex, Skeleton, Spin, Tag, Typography } from 'antd'
import { ArrowUpOutlined, LoadingOutlined, ReloadOutlined } from '@ant-design/icons'
import { PushlyAi, Well } from '@pushly/aqe/lib/components'
import { PQueryBuilder } from '@pushly/aqe/lib/components/pquery-builder'
import { CompiledDirective } from '@pushly/models/lib/structs/pushly-ai/compiled-directive'
import { DirectivePromotionSuggestion } from '@pushly/models/lib/structs/pushly-ai/directive-promotion-suggestion'
import { CONTENT_DIRECTIVE_FIELDS } from './content-directive-fields'
import { getClassNames } from '../../_utils/classnames'
import { BRAND_VOICE_TIER_VISUAL } from './brand-voice-tier-visual'
import {
    collapsedGuaranteedSpec,
    conditionIdentity,
    guaranteedDisplayRows,
    guaranteedRowKeyIndex,
    guidanceSpanKey,
} from './text-highlight'
import { dimensionByBindingId } from './promotion-suggestions'
import './brand-voice-decomposition.scss'

export type CompileState = 'idle' | 'compiling' | 'done' | 'error'

/** Marks a row that is linked to a span, so a click on it is NOT "outside" the selection. Shared
 * with `BrandVoiceEditor`'s click-outside dismissal — see `LINKED_ELEMENT_SELECTOR`.
 */
export const LINKED_ROW_CLASS = 'sw-v2-bvd-linked-row'

/** Everything a click can land on without dismissing a pinned rule: a linked InfoRow, a
 * PQueryBuilder condition row (Guaranteed), or a highlighted mark in the text itself.
 */
export const LINKED_ELEMENT_SELECTOR = `.${LINKED_ROW_CLASS}, .aqe-pqb-rule-row, mark[data-span-key]`

const COMPILING_PHRASES = [
    'Reading through your Brand Voice…',
    'Splitting guarantees from best-effort guidance…',
    'Checking for emojis, exclamations, and banned words…',
    'Deciding what an AI judge should verify…',
    'Wrapping up the details…',
]
const COMPILING_PHRASE_INTERVAL_MS = 2800
const COMPILING_PHRASE_FADE_MS = 250

/**
 * Cycles through `phrases`, fading out briefly before swapping to the next one, only while
 * `active`. Resets to the first phrase whenever `active` turns true, so every new compile
 * starts the sequence fresh rather than resuming mid-cycle from the last one.
 */
function useCyclingPhrase(
    phrases: string[],
    intervalMs: number,
    fadeMs: number,
    active: boolean,
): { phrase: string; visible: boolean } {
    const [index, setIndex] = useState(0)
    const [visible, setVisible] = useState(true)

    useEffect(() => {
        if (!active) return

        setIndex(0)
        setVisible(true)

        let fadeTimeoutId: ReturnType<typeof setTimeout>
        const intervalId = setInterval(() => {
            setVisible(false)
            fadeTimeoutId = setTimeout(() => {
                setIndex((i) => (i + 1) % phrases.length)
                setVisible(true)
            }, fadeMs)
        }, intervalMs)
        return () => {
            clearInterval(intervalId)
            clearTimeout(fadeTimeoutId)
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps -- `phrases` is a module-level constant
    }, [active, intervalMs, fadeMs])

    return { phrase: phrases[index], visible }
}

/**
 * The platform's "an AI call is running" indicator, plus the rotating phrase.
 *
 * `PushlyAi animate` is the established treatment — `use-notification-content-loading-view` uses
 * exactly this for notification generation — so an author who has watched the notification form
 * think already recognises it here. Reused rather than reinvented precisely so it can become the
 * one signal that means "the AI is working", instead of a spinner that means anything.
 */
export const AiCompilingIndicator: FC<{ active?: boolean; size?: number }> = ({ active = true, size = 40 }) => {
    const { phrase, visible } = useCyclingPhrase(
        COMPILING_PHRASES,
        COMPILING_PHRASE_INTERVAL_MS,
        COMPILING_PHRASE_FADE_MS,
        active,
    )

    return (
        <Flex vertical align="center" gap={8} className={getClassNames('bvd-ai-indicator')}>
            <PushlyAi animate className={getClassNames('bvd-ai-indicator-mark')} style={{ fontSize: size }} />
            <Typography.Text
                type="secondary"
                className={getClassNames('bvd-compiling-phrase')}
                style={{
                    opacity: visible ? 1 : 0,
                    transition: `opacity ${COMPILING_PHRASE_FADE_MS}ms ease-in-out`,
                }}
            >
                {phrase}
            </Typography.Text>
        </Flex>
    )
}

interface IBrandVoiceDecomposition {
    state: CompileState
    compiled: CompiledDirective | null
    onRetry: () => void
    /**
     * A compile is running, but `compiled` already holds something worth looking at — keep it on
     * screen with a quiet indicator instead of replacing it with the full loading state.
     *
     * Exists for the promotion flow: confirming a promotion updates the decomposition locally and
     * kicks off the real compile behind it. Blanking to a spinner at that moment would hide the
     * author's own change for ten seconds, which is the entire thing the local update avoids.
     */
    refreshing?: boolean
    /** The span key (see text-highlight.ts) currently linked to the textarea — hovering a
     * Checked/Needs-review row that has one highlights its source text; moving the caret into
     * a highlighted span in the textarea highlights the row back. Omit to render without the
     * highlight wiring (e.g. anywhere this component is used outside the editor drawer).
     */
    activeSpanKey?: string | null
    /** Keys (from findVerbatimSpans) that actually resolved to a span — a row only gets hover
     * wiring when its key is in this set, so a paraphrased item that couldn't be located just
     * renders as a normal, non-interactive row instead of linking to nothing.
     */
    matchedSpanKeys?: Set<string>
    onSpanHover?: (key: string | null) => void
    /** Clicking (not just hovering) a matched row also asks the textarea to scroll its source
     * text into view — a bigger, more deliberate ask than the hover-driven highlight, since a
     * click can happen from anywhere the eye lands without the textarea itself in view. Omit
     * alongside `onSpanHover` to render without highlight wiring at all.
     */
    onSpanClick?: (key: string) => void
    /**
     * Promotion suggestions grouped by the statement they are about, keyed on that
     * statement's exact text. Keyed by text rather than by tier+index because the tier a
     * statement lands in is the compiler's call and can move between compiles, while the
     * author's own sentence is what both sides actually agree on.
     *
     * Rendered on Checked, Guidance and Needs-review rows alike. Guidance carries almost all of
     * them in practice — measurement found vague length rules compiling to `style`, not
     * `assertions`, which makes them MORE worth promoting: guidance is never verified at all.
     */
    suggestionsByItem?: Map<string, DirectivePromotionSuggestion[]>
    /**
     * Opens the confirm-and-adjust step for one statement's suggestions. Receives the whole
     * group, since an ambiguous statement's alternatives are choices WITHIN that step rather
     * than separate things to promote.
     *
     * The row deliberately does not apply anything itself. The number becomes a hard limit on
     * the author's content, and offering it as a single click with no way to change the field,
     * the unit or the value made the most consequential part of the feature something to accept
     * or ignore. Omit to render suggestions read-only.
     */
    onPromote?: (suggestions: DirectivePromotionSuggestion[]) => void
}

/** True when the compiled spec is a non-empty PQuery condition group. */
function specHasConditions(spec: Record<string, unknown> | undefined): boolean {
    if (!spec) return false
    const group = (spec.and ?? spec.or) as unknown[] | undefined
    return Array.isArray(group) && group.length > 0
}

interface ISection {
    icon: React.ReactNode
    title: string
    caption: string
    count: number
    color: string
    /**
     * Full colored border + tinted background — reserved for a section that's genuinely an
     * exception worth flagging (Needs review). Guaranteed/Checked/Guidance are just three
     * flavors of "normal, expected content" competing for no one's urgent attention, so they
     * stay in the same neutral card chrome the rest of the product uses (see the Vespa
     * recommendation config's "Configuration Summary" card) — color there lives only on the
     * icon, not the whole box. Uniformly coloring all four would spend color-as-a-signal on
     * three sections that don't need it, diluting the one that does.
     */
    emphasized?: boolean
    children: React.ReactNode
}

// Uses the app's own `Well` (variant="outline", padding="small") rather than a hand-rolled
// bordered `div`, so this card tracks the platform's real theme tokens (including dark mode)
// instead of a set of manually-picked CSS var fallbacks that can silently drift from them.
export const InfoRow: FC<{
    children: React.ReactNode
    icon?: React.ReactNode
    style?: CSSProperties
    className?: string
    /** Outlines the row in `highlightColor` — set when this row's span key matches the
     * textarea<->rule highlight linking's active key. */
    highlighted?: boolean
    highlightColor?: string
    onMouseEnter?: () => void
    onMouseLeave?: () => void
    onClick?: () => void
}> = ({ children, icon, style, className, highlighted, highlightColor, onMouseEnter, onMouseLeave, onClick }) => (
    <Well
        className={className}
        style={{
            ...style,
            ...(highlighted && { boxShadow: `0 0 0 1px ${highlightColor ?? 'var(--ant-color-primary, #1677ff)'}` }),
            ...(onClick && { cursor: 'pointer' }),
        }}
        variant="outline"
        padding="small"
        shadow={false}
        hideHeader
        onMouseEnter={onMouseEnter}
        onMouseLeave={onMouseLeave}
        onClick={onClick}
    >
        <Flex align="flex-start" gap={8}>
            {icon && <span className={getClassNames('bvd-info-row-icon')}>{icon}</span>}
            <div style={{ flex: 1, minWidth: 0 }}>{children}</div>
        </Flex>
    </Well>
)

const Section: FC<ISection> = ({ icon, title, caption, count, color, emphasized, children }) => (
    <Well
        variant="outline"
        padding="small"
        titleSize="sm"
        shadow={false}
        className={getClassNames('bvd-section')}
        style={
            emphasized
                ? {
                      borderColor: color,
                      // Mixed toward the CONTAINER token rather than literal white. Mixing to
                      // white made a near-white card: a faint tint on a light page, and an
                      // unreadable slab under dark-mode text. Against the token it is that same
                      // faint tint in light and a subtly warmed dark surface in dark, because it
                      // starts from whatever the page is actually made of.
                      backgroundColor: `color-mix(in srgb, ${color}, var(--ant-color-bg-container) 95%)`,
                  }
                : undefined
        }
        title={
            <Flex vertical gap={2}>
                <Flex align="center" gap={8} className={getClassNames('bvd-section-header')}>
                    <span style={{ color, display: 'inline-flex' }}>{icon}</span>
                    <Typography.Text strong>{title}</Typography.Text>
                    <Tag bordered={false}>{count}</Tag>
                </Flex>
                <Typography.Text type="secondary" className={getClassNames('bvd-section-caption')}>
                    {caption}
                </Typography.Text>
            </Flex>
        }
    >
        <div className={getClassNames('bvd-section-body')}>{children}</div>
    </Well>
)

/**
 * The subdued "this could be a guarantee" affordance under a prose-tier rule.
 *
 * An author who writes "Keep titles short" gets guidance applied best-effort and never verified.
 * The same intent with a number in it compiles to a deterministic binding. Nothing in the
 * product told them that, and this is the one place the gap is visible — the rule and the tier
 * it landed in are already on screen together.
 *
 * Styled as quiet supporting text inside the row it belongs to, NOT as a callout: this is
 * optional advice about text that is already working, and the decomposition has been kept
 * deliberately calm. It borrows the Guaranteed tier's color for the icon alone, so the affordance
 * says where the rule would MOVE TO without competing with the Needs-review section, which is the
 * one thing in this view that has earned an alarm color.
 */
const PromotionSuggestion: FC<{
    suggestions: DirectivePromotionSuggestion[]
    onPromote?: (suggestions: DirectivePromotionSuggestion[]) => void
}> = ({ suggestions, onPromote }) => {
    if (!suggestions.length || !onPromote) return null

    const lead = suggestions[0]
    // More than one entry means the statement named a length but no field, so these are
    // ALTERNATIVES for one sentence — see `groupSuggestionsByItem`. The row states that a choice
    // exists; the choice itself is made in the confirm step, where it sits next to the number it
    // changes instead of being frozen into a button label.
    const isChoice = suggestions.length > 1
    const dimension = dimensionByBindingId(lead.binding_id)

    return (
        <div className={getClassNames('bvd-promotion')}>
            <Flex align="flex-start" gap={6}>
                <span
                    className={getClassNames('bvd-promotion-icon')}
                    style={{ color: BRAND_VOICE_TIER_VISUAL.guaranteed.color }}
                >
                    <ArrowUpOutlined />
                </span>
                <Flex vertical gap={2} style={{ flex: 1, minWidth: 0 }}>
                    <Typography.Text type="secondary" className={getClassNames('bvd-promotion-text')}>
                        {isChoice ? (
                            <>
                                This could be guaranteed as a{' '}
                                {/* The UNIT, but not the number or the field. Both of those depend
                                    on a choice the author has not made yet, and the row was
                                    showing the lead suggestion's — "a limit on 99 characters"
                                    over a rationale that said "your titles", which answers the
                                    very question the next click is about to ask. */}
                                <Typography.Text className={getClassNames('bvd-promotion-text')}>
                                    {dimension?.unit === 'words' ? 'word' : 'character'} limit
                                </Typography.Text>
                                .
                            </>
                        ) : (
                            <>
                                This could be guaranteed:{' '}
                                <Typography.Text className={getClassNames('bvd-promotion-text')}>
                                    “{lead.suggested_text}”
                                </Typography.Text>
                            </>
                        )}
                    </Typography.Text>
                    {/* The number is the author's to judge, not to trust — it becomes a hard
                        limit on their own content, so where it came from is shown inline rather
                        than hidden behind a tooltip. Omitted for a choice: every figure in it is
                        specific to one field, and there is no field yet. */}
                    {!isChoice && (
                        <Typography.Text type="secondary" className={getClassNames('bvd-promotion-rationale')}>
                            {lead.rationale}
                        </Typography.Text>
                    )}
                    <div>
                        {/* The row this sits in owns a click handler that pins the rule's source
                            text. Promoting is a different intent, so it must not also toggle
                            that pin on its way up. */}
                        <Button
                            size="small"
                            type="link"
                            onClick={(e) => {
                                e.stopPropagation()
                                onPromote(suggestions)
                            }}
                        >
                            {isChoice ? 'Choose a limit…' : 'Review and apply…'}
                        </Button>
                    </div>
                </Flex>
            </Flex>
        </div>
    )
}

/**
 * Read-only decomposition of a compiled Brand Voice into its four tiers:
 *   - Guaranteed  -> `spec`       (deterministic PQuery, rendered via PQueryBuilder)
 *   - Checked     -> `assertions` (mandates verified by an AI judge at generation)
 *   - Guidance    -> `style`      (best-effort, applied verbatim)
 *   - Needs review-> `unmapped`   (stated but fits no rule/assertion)
 *
 * Per-rule editing, promotion and conflict resolution are intentionally out of scope: this
 * view shows the author what their words compiled into, and nothing more.
 */
export const BrandVoiceDecomposition: FC<IBrandVoiceDecomposition> = ({
    state,
    compiled,
    onRetry,
    refreshing = false,
    activeSpanKey = null,
    matchedSpanKeys,
    onSpanHover,
    onSpanClick,
    suggestionsByItem,
    onPromote,
}) => {
    if (state === 'idle') {
        return (
            <Empty
                image={Empty.PRESENTED_IMAGE_SIMPLE}
                description="Start describing your Brand Voice to see how it breaks down."
            />
        )
    }

    // A background refresh over existing content never takes the panel away — see `refreshing`.
    if (state === 'compiling' && !(refreshing && compiled)) {
        return (
            <Flex vertical gap={12} className={getClassNames('bvd-status')}>
                <AiCompilingIndicator />

                {/* Only reachable on a FIRST compile — re-analyzing keeps the existing
                    decomposition under an overlay instead. So this is someone who has never seen
                    the result, staring at an empty panel for ten seconds. Showing the shape of
                    what is coming answers the question the wait creates: what is this panel even
                    going to say. */}
                {[
                    BRAND_VOICE_TIER_VISUAL.guaranteed,
                    BRAND_VOICE_TIER_VISUAL.checked,
                    BRAND_VOICE_TIER_VISUAL.guidance,
                ].map((tier, i) => (
                    <Well key={i} variant="outline" padding="small" shadow={false} hideHeader>
                        <Flex align="center" gap={8}>
                            <span style={{ color: tier.color, display: 'inline-flex' }}>{tier.icon}</span>
                            <Skeleton
                                active
                                title={{ width: 120 }}
                                paragraph={{ rows: 2, width: ['100%', '60%'] }}
                                className={getClassNames('bvd-skeleton')}
                            />
                        </Flex>
                    </Well>
                ))}
            </Flex>
        )
    }

    if (state === 'error' || !compiled) {
        return (
            <Alert
                type="error"
                showIcon
                message="Couldn't analyze your Brand Voice"
                description="The directive compiler didn't respond. Your text is safe — nothing was changed. Until this succeeds, the AI falls back to your last saved rules."
                action={
                    <Button size="small" icon={<ReloadOutlined />} onClick={onRetry}>
                        Retry
                    </Button>
                }
            />
        )
    }

    // Condition-identity -> span key, so a PQueryBuilder row can find its own `resolved` entry
    // without relying on row position (see `guaranteedRowKeyIndex`).
    const guaranteedRowKeys = guaranteedRowKeyIndex(compiled)

    // The rows to SHOW, which is not one per PQuery condition — `banned_terms` fans out to one
    // condition per term. See `guaranteedDisplayRows`.
    //
    // Memoized because `guaranteedSpec` is handed to PQueryBuilder, whose provider re-initializes
    // on the prop's IDENTITY: a fresh object re-parses the whole document and resets the store.
    // This component re-renders on every hover (the well owns `hoveredSpanKey`), so computing
    // these inline re-parsed a read-only builder on each one.
    const guaranteedRows = useMemo(() => guaranteedDisplayRows(compiled), [compiled])
    const guaranteedSpec = useMemo(() => collapsedGuaranteedSpec(compiled), [compiled])

    const hasGuaranteed = specHasConditions(guaranteedSpec)
    const assertions = compiled.assertions ?? []
    const style = compiled.style ?? []
    const unmapped = compiled.unmapped ?? []

    return (
        <Flex vertical gap={16} className={getClassNames('brand-voice-decomposition')}>
            {refreshing && (
                // Deliberately understated. The content below is a local prediction of what the
                // compiler will say, and it is very nearly always right — presenting that as a
                // warning would make the common case feel like a problem. It states what is
                // happening and nothing more.
                <Flex align="center" gap={8} className={getClassNames('bvd-refreshing')}>
                    <Spin size="small" indicator={<LoadingOutlined spin />} />
                    <Typography.Text type="secondary">Re-checking your Brand Voice…</Typography.Text>
                </Flex>
            )}
            <div data-bvd-tier="guaranteed">
                <Section
                    icon={BRAND_VOICE_TIER_VISUAL.guaranteed.icon}
                    title="Guaranteed"
                    caption="Deterministically checked and enforced on every generation."
                    // Rows, not conditions. Counting conditions reported nine for what the author
                    // wrote as three sentences, because a term list fans out — a number they had no
                    // way to reconcile with their own text.
                    count={guaranteedRows.length}
                    color={BRAND_VOICE_TIER_VISUAL.guaranteed.color}
                >
                    {hasGuaranteed ? (
                        <PQueryBuilder
                            value={guaranteedSpec}
                            fields={CONTENT_DIRECTIVE_FIELDS}
                            disabled
                            // Overrides two tokens aqe re-declares on the builder's own root — see
                            // brand-voice-decomposition.scss's `.bvd-guaranteed-pqb` for why this
                            // has to be a class on the builder itself (via its own documented
                            // `className` prop) rather than a CSS variable set on a wrapping div.
                            className={getClassNames('bvd-guaranteed-pqb')}
                            // Required unconditionally outside a PQueryBuilderProvider — even in
                            // `disabled` mode, the library throws without it. A no-op is correct
                            // here, not a gap to fill in: this view is read-only by design, and
                            // per-rule editing is out of scope (see the docstring above).
                            onRuleCommit={() => {}}
                            // The section caption above already states "All of the following are
                            // true" (PQueryBuilder's own group header) — repeating "AND" beside
                            // every row below the first says the same thing again. Blank just the
                            // per-row connector; the group header (unaffected by this prop) stays.
                            labels={{ connectors: { and: '', or: '' } }}
                            // Links each enforced condition back to the sentence that produced it,
                            // the same hover/click behavior the Checked and Needs-review rows have.
                            // Those are InfoRows we render ourselves and can wire directly; a
                            // PQueryBuilder row is only reachable through aqe's `rowProps` seam.
                            // Matched by condition identity, never by row position — see
                            // `guaranteedRowKeyIndex`.
                            rowProps={({ property, operator, value }) => {
                                const key = guaranteedRowKeys.get(conditionIdentity(property, operator, value))
                                if (!key || !matchedSpanKeys?.has(key)) return undefined
                                return {
                                    className: activeSpanKey === key ? getClassNames('bvd-pqb-row-active') : undefined,
                                    onMouseEnter: () => onSpanHover?.(key),
                                    onMouseLeave: () => onSpanHover?.(null),
                                    onClick: () => onSpanClick?.(key),
                                }
                            }}
                        />
                    ) : (
                        <Typography.Text type="secondary">
                            No enforceable rules yet. Phrases like <Typography.Text code>never…</Typography.Text>,{' '}
                            <Typography.Text code>always…</Typography.Text>, or{' '}
                            <Typography.Text code>under 60 characters</Typography.Text> become guarantees.
                        </Typography.Text>
                    )}
                </Section>
            </div>

            <div data-bvd-tier="checked">
                <Section
                    icon={BRAND_VOICE_TIER_VISUAL.checked.icon}
                    title="Checked by AI review"
                    caption="Mandates we can't measure directly — an AI judge verifies each generation against them, in your words."
                    count={assertions.length}
                    color={BRAND_VOICE_TIER_VISUAL.checked.color}
                >
                    {assertions.length > 0 ? (
                        <ul className={getClassNames('bvd-assertions-list')}>
                            {assertions.map((a, i) => {
                                const key = `checked-${i}`
                                const matched = matchedSpanKeys?.has(key) ?? false
                                return (
                                    <li key={i}>
                                        <InfoRow
                                            className={matched ? LINKED_ROW_CLASS : undefined}
                                            highlighted={activeSpanKey === key}
                                            highlightColor={BRAND_VOICE_TIER_VISUAL.checked.color}
                                            onMouseEnter={matched ? () => onSpanHover?.(key) : undefined}
                                            onMouseLeave={matched ? () => onSpanHover?.(null) : undefined}
                                            onClick={matched ? () => onSpanClick?.(key) : undefined}
                                        >
                                            <Typography.Text>{a.text}</Typography.Text>
                                            <PromotionSuggestion
                                                suggestions={suggestionsByItem?.get(a.text) ?? []}
                                                onPromote={onPromote}
                                            />
                                        </InfoRow>
                                    </li>
                                )
                            })}
                        </ul>
                    ) : (
                        <Typography.Text type="secondary">No judged assertions.</Typography.Text>
                    )}
                </Section>
            </div>

            <div data-bvd-tier="guidance">
                <Section
                    icon={BRAND_VOICE_TIER_VISUAL.guidance.icon}
                    title="Guidance"
                    caption="Applied best-effort. We steer toward this, in your words — never reworded."
                    count={style.length}
                    color={BRAND_VOICE_TIER_VISUAL.guidance.color}
                >
                    {style.length > 0 ? (
                        <Flex vertical gap={8}>
                            {style.map((g: any, i: number) => {
                                // One row per statement, like Checked and Needs review. `style` used
                                // to be a single synthesized paragraph, so this section was one row
                                // whose hover lit every source at once; now each statement carries
                                // its own quote and links to just that sentence.
                                const key = guidanceSpanKey(i)
                                const matched = matchedSpanKeys?.has(key) ?? false
                                return (
                                    <InfoRow
                                        key={i}
                                        className={matched ? LINKED_ROW_CLASS : undefined}
                                        highlighted={activeSpanKey === key}
                                        highlightColor={BRAND_VOICE_TIER_VISUAL.guidance.color}
                                        onMouseEnter={matched ? () => onSpanHover?.(key) : undefined}
                                        onMouseLeave={matched ? () => onSpanHover?.(null) : undefined}
                                        onClick={matched ? () => onSpanClick?.(key) : undefined}
                                    >
                                        <Typography.Paragraph style={{ marginBottom: 0, whiteSpace: 'pre-wrap' }}>
                                            “{g.text}”
                                        </Typography.Paragraph>
                                        <PromotionSuggestion
                                            suggestions={suggestionsByItem?.get(g.text) ?? []}
                                            onPromote={onPromote}
                                        />
                                    </InfoRow>
                                )
                            })}
                        </Flex>
                    ) : (
                        <Typography.Text type="secondary">No stylistic guidance yet.</Typography.Text>
                    )}
                </Section>
            </div>

            {unmapped.length > 0 && (
                <div data-bvd-tier="needsReview">
                    <Section
                        icon={BRAND_VOICE_TIER_VISUAL.needsReview.icon}
                        title="Needs review"
                        caption="Stated clearly, but it fits no rule or assertion we can apply — surfaced so nothing is silently dropped."
                        count={unmapped.length}
                        color={BRAND_VOICE_TIER_VISUAL.needsReview.color}
                        emphasized
                    >
                        <Flex vertical gap={8}>
                            {unmapped.map((u, i) => {
                                const key = `needsReview-${i}`
                                const matched = matchedSpanKeys?.has(key) ?? false
                                return (
                                    <InfoRow
                                        key={i}
                                        className={matched ? LINKED_ROW_CLASS : undefined}
                                        icon={BRAND_VOICE_TIER_VISUAL.needsReview.icon}
                                        highlighted={activeSpanKey === key}
                                        highlightColor={BRAND_VOICE_TIER_VISUAL.needsReview.color}
                                        onMouseEnter={matched ? () => onSpanHover?.(key) : undefined}
                                        onMouseLeave={matched ? () => onSpanHover?.(null) : undefined}
                                        onClick={matched ? () => onSpanClick?.(key) : undefined}
                                    >
                                        <Typography.Text>“{u.text}”</Typography.Text>
                                        <PromotionSuggestion
                                            suggestions={suggestionsByItem?.get(u.text) ?? []}
                                            onPromote={onPromote}
                                        />
                                    </InfoRow>
                                )
                            })}
                        </Flex>
                    </Section>
                </div>
            )}
        </Flex>
    )
}

export type BrandVoiceTierKey = 'guaranteed' | 'checked' | 'guidance' | 'needsReview'

interface IBrandVoiceSummaryChips {
    compiled: CompiledDirective
    /**
     * When provided (alongside `onTierToggle`), the active tier's chip is emphasized. What a
     * click MEANS is the caller's to decide: the settings card filters its text to that tier,
     * while the editor drawer scrolls to that tier's section. Both are "show me this tier",
     * and neither belongs in a component whose job is to render the summary.
     *
     * Omit both to render plain, non-interactive chips.
     */
    activeTier?: BrandVoiceTierKey | null
    onTierToggle?: (tier: BrandVoiceTierKey) => void
}

/**
 * Compact, glanceable summary of a compiled directive — the counts a settings-page card
 * should lead with instead of the raw free-text body, which explains what was said but not
 * what actually got enforced. Reads persisted `compiled_rules` only; never triggers a compile.
 *
 * Doubles as a filter: clicking a chip asks the caller to show only
 * that tier's highlights in the paired text view; clicking the same chip again clears it.
 */
export const BrandVoiceSummaryChips: FC<IBrandVoiceSummaryChips> = ({ compiled, activeTier, onTierToggle }) => {
    // The same row count the Guaranteed section shows. `resolved.length` counted fanned-out
    // PQuery conditions, so the strip read "7 Guaranteed" directly above a section headed
    // "Guaranteed 3" — two numbers for one thing, on screen together.
    const guaranteedCount = useMemo(() => guaranteedDisplayRows(compiled), [compiled]).length
    const checkedCount = compiled.assertions?.length ?? 0
    const guidanceCount = compiled.style?.length ?? 0
    const needsReviewCount = compiled.unmapped?.length ?? 0

    const chips = [
        { key: 'guaranteed', ...BRAND_VOICE_TIER_VISUAL.guaranteed, text: `${guaranteedCount} Guaranteed` },
        { key: 'checked', ...BRAND_VOICE_TIER_VISUAL.checked, text: `${checkedCount} Checked` },
        // A count, like every other chip. "Guidance set" dates from when `style` was one
        // synthesized paragraph and a number meant nothing; it is a list of individual
        // statements now, and the count is as informative as the others.
        guidanceCount > 0
            ? { key: 'guidance', ...BRAND_VOICE_TIER_VISUAL.guidance, text: `${guidanceCount} Guidance` }
            : null,
        needsReviewCount > 0
            ? { key: 'needsReview', ...BRAND_VOICE_TIER_VISUAL.needsReview, text: `${needsReviewCount} needs review` }
            : null,
    ].filter((chip): chip is NonNullable<typeof chip> & { key: BrandVoiceTierKey } => chip !== null)

    const filterable = !!onTierToggle

    return (
        <Flex gap={8} wrap="wrap">
            {chips.map((chip) => {
                const isActive = activeTier === chip.key
                return (
                    <Tag
                        key={chip.key}
                        bordered={isActive}
                        onClick={filterable ? () => onTierToggle!(chip.key) : undefined}
                        style={{
                            color: chip.color,
                            background: isActive
                                ? `color-mix(in srgb, ${chip.color}, transparent 88%)`
                                : // Black at 3.5% is a soft grey on a light page and invisible on
                                  // a dark one. The fill token is the same idea expressed in
                                  // whichever theme is actually rendering.
                                  'var(--ant-color-fill-quaternary)',
                            borderColor: isActive ? chip.color : undefined,
                            cursor: filterable ? 'pointer' : undefined,
                        }}
                    >
                        <Flex align="center" gap={4} style={{ display: 'inline-flex' }}>
                            <span style={{ display: 'inline-flex', fontSize: 12 }}>{chip.icon}</span>
                            <span>{chip.text}</span>
                        </Flex>
                    </Tag>
                )
            })}
        </Flex>
    )
}
